'use client';

import type { ReactNode } from 'react';
import {
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import type {
  GetRowIdParams,
  GridSizeChangedEvent,
  StateUpdatedEvent,
  GridReadyEvent,
  GridApi,
  GridState,
  Column,
  RowClickedEvent,
  ThemeDefaultParams,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { registerTerraAgGridModules } from './ag-grid-community-modules';
import { token } from '@terra/ui-styled-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useThemeMode } from '../../custom-components';
import { useTablePersistence } from '../../hooks/useTablePersistence';
import type {
  TableId,
  ExternalFilters,
} from '../../utils/table-persistence.utils';
import type { ColumnInfo } from './column-visibility-panel';
import { ColumnVisibilityPanel } from './column-visibility-panel';
import { IconButton } from '../icon-button';
import { Tooltip } from '../tooltip';
import { FilterRemoveIcon } from '@terra/ui-icons';
import { isInteractiveElement } from './utils/isInteractiveElement';
import { useAutoPinActionsColumn } from '../../hooks/useAutoPinActionsColumn';
import { useAgGridHeaderScrollSync } from '../../hooks/useAgGridHeaderScrollSync';
import { Spinner } from '../spinner';
import { wrapColumnDefsWithTableCellLayout } from '../table-cell-content';
import {
  applyGridStateToApi,
  resolvePersistedGridState,
  type TablePersistedState,
} from './table-grid-state';

const baseTerraTheme = themeQuartz.withParams({
  accentColor: token('colors.terra.9'),
  fontFamily: 'var(--font-dm-sans)',
});

registerTerraAgGridModules();
type TableProps<T> = AgGridReactProps<T> & {
  noBorder?: boolean;
  backgroundColor?: string;
  fontSize?: number;
  headerFontSize?: number;
  themeParams?: Partial<ThemeDefaultParams>;
  persistence?: {
    tableId: TableId;
    applicationId?: string;
    userId?: string;
    externalFilters?: ExternalFilters;
    onExternalFiltersRestore?: (filters: ExternalFilters) => void;
    onPersistenceReset?: () => void;
    onPersistedStateApplied?: (api: GridApi) => void;
    persistedState?: TablePersistedState;
  };
  customHeader?: ReactNode;
  hideColumnControls?: boolean;
  autoPinActionsColumn?: boolean;
  fitColumnsOnGridSizeChanged?: boolean;
  preventDefaultOnContextMenu?: boolean;
  gridKey?: string;
};

function Table<T>({
  noBorder = false,
  backgroundColor,
  fontSize,
  headerFontSize,
  themeParams,
  customHeader,
  persistence,
  hideColumnControls = false,
  columnDefs,
  onRowClicked,
  autoPinActionsColumn = false,
  fitColumnsOnGridSizeChanged = true,
  preventDefaultOnContextMenu = false,
  gridKey,
  ...props
}: TableProps<T>) {
  const { mode } = useThemeMode();
  const theme = useMemo(() => {
    const params: Record<string, unknown> = {};

    if (noBorder) {
      params.borderRadius = 0;
      params.wrapperBorderRadius = 0;
      params.wrapperBorder = false;
    }

    if (backgroundColor !== undefined) {
      params.headerBackgroundColor = 'transparent';
      params.backgroundColor = backgroundColor;
    } else {
      params.backgroundColor = token('colors.bg.default');
    }

    if (fontSize !== undefined) {
      params.fontSize = fontSize;
    }

    if (headerFontSize !== undefined) {
      params.headerFontSize = headerFontSize;
    }

    const mergedParams = { ...params, ...themeParams };

    if (Object.keys(mergedParams).length > 0) {
      return baseTerraTheme.withParams(mergedParams);
    }

    return baseTerraTheme;
  }, [noBorder, backgroundColor, fontSize, headerFontSize, themeParams]);
  const t = useTranslations('designSystem.table');
  const userId = persistence?.userId;
  const initializationCompleteRef = useRef(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [prevGridKey, setPrevGridKey] = useState(gridKey);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const previousExternalFiltersRef = useRef<ExternalFilters | undefined>(
    undefined,
  );

  if (gridKey !== prevGridKey) {
    setPrevGridKey(gridKey);
    setGridApi(null);
    initializationCompleteRef.current = false;
  }

  const wasMountedWithoutPersistenceRef = useRef(!persistence);
  const isRestoringStateRef = useRef(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const showColumnControls = !!persistence && !hideColumnControls;
  const showHeader = !!customHeader || showColumnControls;
  const { savedState, handleSaveState, hasInitialized, resetPersistence } =
    useTablePersistence({
      tableId: persistence?.tableId || ('unused' as TableId),
      applicationId: persistence?.applicationId,
      userId: persistence?.userId,
      externalFilters: persistence?.externalFilters,
      onExternalFiltersRestore: persistence?.onExternalFiltersRestore,
    });
  const persistedState = persistence?.persistedState ?? 'full';
  const restoredState = useMemo(
    () =>
      savedState
        ? resolvePersistedGridState({ state: savedState, persistedState })
        : undefined,
    [persistedState, savedState],
  );
  const lastSavedLayoutRef = useRef<string | null>(null);
  const saveGridState = useCallback(
    (state: GridState) => {
      const nextState = resolvePersistedGridState({ state, persistedState });

      if (persistedState === 'columnLayout') {
        const serializedState = JSON.stringify(nextState);

        if (serializedState === lastSavedLayoutRef.current) {
          return;
        }

        lastSavedLayoutRef.current = serializedState;
      }

      handleSaveState(nextState);
    },
    [handleSaveState, persistedState],
  );
  const {
    checkHorizontalScroll,
    onGridReadyHandler,
    getColumnDefsWithActionsColumn,
  } = useAutoPinActionsColumn({
    enabled: autoPinActionsColumn,
    gridApi,
    gridContainerRef,
  });

  useAgGridHeaderScrollSync({
    gridApi,
    gridContainerRef,
  });
  const updateColumnsForVisibilityPanel = useCallback((api: GridApi) => {
    const allColumns = api.getColumns();

    if (allColumns) {
      const columnInfo: ColumnInfo[] = allColumns.map((col: Column) => ({
        colId: col.getColId(),
        headerName: col.getColDef().headerName || col.getColId(),
        visible: col.isVisible(),
      }));

      setColumns(columnInfo);
    }
  }, []);

  useEffect(() => {
    initializationCompleteRef.current = false;
  }, [userId]);
  useLayoutEffect(() => {
    if (
      wasMountedWithoutPersistenceRef.current &&
      restoredState &&
      persistence &&
      gridApi &&
      !gridApi.isDestroyed()
    ) {
      wasMountedWithoutPersistenceRef.current = false;
      isRestoringStateRef.current = true;
      applyGridStateToApi({
        api: gridApi,
        state: restoredState,
        persistedState,
      });
      isRestoringStateRef.current = false;
      initializationCompleteRef.current = true;
      updateColumnsForVisibilityPanel(gridApi);
      persistence.onPersistedStateApplied?.(gridApi);
    }
  }, [
    restoredState,
    persistence,
    persistedState,
    gridApi,
    updateColumnsForVisibilityPanel,
  ]);
  useEffect(() => {
    if (
      persistence?.externalFilters &&
      persistence?.userId &&
      hasInitialized.current
    ) {
      const filtersChanged =
        JSON.stringify(previousExternalFiltersRef.current) !==
        JSON.stringify(persistence.externalFilters);

      if (filtersChanged) {
        if (!gridApi || gridApi.isDestroyed()) {
          return;
        }

        handleSaveState(gridApi.getState());
        previousExternalFiltersRef.current = persistence.externalFilters;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    persistence?.externalFilters,
    handleSaveState,
    persistence?.userId,
    gridApi,
  ]);
  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      updateColumnsForVisibilityPanel(event.api);
      onGridReadyHandler(event.api);
      props.onGridReady?.(event);
    },
    [props, updateColumnsForVisibilityPanel, onGridReadyHandler],
  );
  const onStateUpdated = useCallback(
    (params: StateUpdatedEvent<T>) => {
      if (!initializationCompleteRef.current || isRestoringStateRef.current) {
        initializationCompleteRef.current = true;

        return;
      }

      if (persistence && params.api) {
        saveGridState(params.api.getState());
      }

      if (params.api) {
        updateColumnsForVisibilityPanel(params.api);
      }
    },
    [saveGridState, persistence, updateColumnsForVisibilityPanel],
  );
  const handleColumnVisibilityChange = useCallback(
    (colId: string, visible: boolean) => {
      setColumns((prev) =>
        prev.map((col) => (col.colId === colId ? { ...col, visible } : col)),
      );
      if (gridApi && persistence && initializationCompleteRef.current) {
        const currentState = gridApi.getState();

        saveGridState(currentState);
      }
    },
    [gridApi, persistence, saveGridState],
  );
  const handleBulkColumnVisibilityChange = useCallback(
    (
      columnUpdates: {
        colId: string;
        visible: boolean;
      }[],
    ) => {
      setColumns((prev) =>
        prev.map((col) => {
          const update = columnUpdates.find((u) => u.colId === col.colId);

          return update ? { ...col, visible: update.visible } : col;
        }),
      );
      if (gridApi && persistence && initializationCompleteRef.current) {
        const currentState = gridApi.getState();

        saveGridState(currentState);
      }
    },
    [gridApi, persistence, saveGridState],
  );
  const handleResetPersistence = useCallback(() => {
    lastSavedLayoutRef.current = null;
    resetPersistence();
    persistence?.onPersistenceReset?.();
    if (gridApi) {
      gridApi.resetColumnState();
      gridApi.setFilterModel(null);
      gridApi.applyColumnState({
        state: [],
        defaultState: { sort: null },
      });
    }
  }, [resetPersistence, persistence, gridApi]);
  const getRowStyle = useCallback(
    () => onRowClicked && { cursor: 'pointer' },
    [onRowClicked],
  );
  const handleRowClicked = useCallback(
    (rowClickedEvent: RowClickedEvent<T>) => {
      const target = rowClickedEvent.event?.target as HTMLElement;

      if (isInteractiveElement(target)) {
        return;
      }

      onRowClicked?.(rowClickedEvent);
    },
    [onRowClicked],
  );
  const handleColumnResized = useCallback(
    (
      event: Parameters<NonNullable<AgGridReactProps<T>['onColumnResized']>>[0],
    ) => {
      props.onColumnResized?.(event);
      if (
        persistence &&
        initializationCompleteRef.current &&
        event.finished &&
        event.api &&
        !event.api.isDestroyed()
      ) {
        saveGridState(event.api.getState());
      }

      checkHorizontalScroll();
    },
    [props, persistence, saveGridState, checkHorizontalScroll],
  );
  const handleGridSizeChanged = useCallback(
    (event: GridSizeChangedEvent<T>) => {
      props.onGridSizeChanged?.(event);
      if (
        fitColumnsOnGridSizeChanged &&
        event.clientWidth > 0 &&
        !event.api.isDestroyed()
      ) {
        event.api.sizeColumnsToFit();
      }
    },
    [fitColumnsOnGridSizeChanged, props],
  );
  const columnDefsWithActionsColumn = useMemo(() => {
    const defs = autoPinActionsColumn
      ? getColumnDefsWithActionsColumn(columnDefs ?? [])
      : columnDefs;

    return wrapColumnDefsWithTableCellLayout(defs);
  }, [autoPinActionsColumn, columnDefs, getColumnDefsWithActionsColumn]);

  if (
    persistence &&
    !hasInitialized.current &&
    !wasMountedWithoutPersistenceRef.current
  ) {
    return (
      <VStack w="full" h="full" gap={0}>
        {showHeader && (
          <HStack pb={6} alignItems="flex-start" w="full" gap={2.5}>
            {customHeader && (
              <Box flex={1} minW={0}>
                {customHeader}
              </Box>
            )}
            {showColumnControls && (
              <HStack gap={2} flexShrink={0}>
                <Box w={8} h={8} />
                <Box w={8} h={8} />
              </HStack>
            )}
          </HStack>
        )}
        <Box
          w="full"
          h="full"
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="md" />
        </Box>
      </VStack>
    );
  }

  return (
    <VStack w="full" h="full" minW={0} overflow="hidden" gap={0}>
      {showHeader && (
        <HStack pb={6} alignItems="flex-start" w="full" gap={2.5}>
          {customHeader && (
            <Box flex={1} minW={0}>
              {customHeader}
            </Box>
          )}
          {showColumnControls && (
            <HStack gap={2} flexShrink={0}>
              <Tooltip title={t('clearFilters')}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('clearFilters')}
                  onClick={handleResetPersistence}
                >
                  <FilterRemoveIcon size={16} />
                </IconButton>
              </Tooltip>
              <ColumnVisibilityPanel
                gridApi={gridApi}
                columns={columns}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onBulkColumnVisibilityChange={handleBulkColumnVisibilityChange}
              />
            </HStack>
          )}
        </HStack>
      )}
      <Box
        ref={gridContainerRef}
        w="full"
        h="full"
        minW={0}
        overflow="hidden"
        flex="1"
        data-ag-theme-mode={mode}
        css={{
          '& .ag-cell-wrapper, & .ag-cell-value': {
            height: '100%',
          },
          '& .ag-cell-value': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <AgGridReact
          key={gridKey}
          theme={theme}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          columnDefs={columnDefsWithActionsColumn}
          {...props}
          initialState={restoredState}
          onGridReady={handleGridReady}
          onStateUpdated={onStateUpdated}
          onColumnResized={handleColumnResized}
          onGridSizeChanged={handleGridSizeChanged}
          maintainColumnOrder
          onRowClicked={handleRowClicked}
          preventDefaultOnContextMenu={preventDefaultOnContextMenu}
        />
      </Box>
    </VStack>
  );
}

function getRowId(params: GetRowIdParams) {
  return params?.data?.id || params?.data?._id || '';
}

export { Table };
